import {
  createHintEscalationState,
  createRetryFeedback,
  createSuccessFeedback,
  type ActivityFeedback,
  type HintEscalationState
} from "../../feedback";
import type { InputMethod } from "../../input";
import type {
  ScenarioChoiceSafety,
  ScenarioNarration,
  ScenarioPayload
} from "./payload";

export type ScenarioStatus = "choosing" | "completed";

export interface ScenarioChoiceOutcome {
  readonly choiceId: string;
  readonly choiceSafety: ScenarioChoiceSafety;
  readonly isBestChoice: boolean;
  readonly supportiveFeedback: ScenarioNarration;
  readonly activityFeedback: ActivityFeedback;
  readonly followUp: ScenarioNarration | null;
}

export interface ScenarioState {
  readonly status: ScenarioStatus;
  readonly selectedChoiceId: string | null;
  readonly lastInputMethod: InputMethod | null;
  readonly attemptCount: number;
  readonly retryCount: number;
  readonly hintState: HintEscalationState;
  readonly outcome: ScenarioChoiceOutcome | null;
}

export interface ScenarioAttemptMetrics {
  readonly correctRate: number;
  readonly hintCount: number;
  readonly retryCount: number;
}

export type ScenarioAction =
  | {
      readonly type: "choose";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "reset" };

export function createScenarioState(): ScenarioState {
  return {
    status: "choosing",
    selectedChoiceId: null,
    lastInputMethod: null,
    attemptCount: 0,
    retryCount: 0,
    hintState: createHintEscalationState(),
    outcome: null
  };
}

export function reduceScenario(
  state: ScenarioState,
  action: ScenarioAction,
  payload: ScenarioPayload
): ScenarioState {
  switch (action.type) {
    case "reset":
      return createScenarioState();
    case "choose": {
      if (state.status === "completed") {
        return state;
      }

      const choice = payload.choices.find(({ id }) => id === action.choiceId);

      if (choice === undefined) {
        return state;
      }

      const isBestChoice =
        choice.id === payload.bestChoiceId && choice.safety === "safe";

      if (isBestChoice) {
        return {
          status: "completed",
          selectedChoiceId: choice.id,
          lastInputMethod: action.inputMethod,
          attemptCount: state.attemptCount + 1,
          retryCount: state.retryCount,
          hintState: state.hintState,
          outcome: {
            choiceId: choice.id,
            choiceSafety: choice.safety,
            isBestChoice: true,
            supportiveFeedback: choice.supportiveFeedback,
            activityFeedback: createSuccessFeedback(),
            followUp: payload.followUp ?? null
          }
        };
      }

      const retry = createRetryFeedback(state.hintState);

      return {
        status: "choosing",
        selectedChoiceId: choice.id,
        lastInputMethod: action.inputMethod,
        attemptCount: state.attemptCount + 1,
        retryCount: state.retryCount + 1,
        hintState: retry.hintState,
        outcome: {
          choiceId: choice.id,
          choiceSafety: choice.safety,
          isBestChoice: false,
          supportiveFeedback: choice.supportiveFeedback,
          activityFeedback: retry.feedback,
          followUp: null
        }
      };
    }
  }
}

export function getScenarioAttemptMetrics(
  state: ScenarioState
): ScenarioAttemptMetrics {
  return {
    correctRate:
      state.attemptCount === 0 || state.status !== "completed"
        ? 0
        : 1 / state.attemptCount,
    hintCount: state.hintState.hintCount,
    retryCount: state.retryCount
  };
}
